import React, { useEffect, useState, useMemo  } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./JoinPageV2.css"; 
import "./PublicPageShared.css";

// Design system primitives (Phase 2)
import {
  Button,
  Input,
  Spinner,
  ThemeToggle,
  toast,
  cn,
  SunIcon, // not used here directly, kept for reference
} from "../../../styles/facemeet-ui";

import {
  Brand,
  ThemeSlot,
  Ambient,
  isValidEmail,
  parseError,
} from "./PublicHelpers";


export function JoinPageV2() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_SERVER_URL;

  const [meetingInfo, setMeetingInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState("");

  const [displayName, setDisplayName] = useState(() => localStorage.getItem("username") || "");
  const [email, setEmail] = useState(() => {
    const stored = localStorage.getItem("userEmail");
    return isValidEmail(stored || "") ? stored : "";
  });
  const [fdaId, setFdaId] = useState(() => localStorage.getItem("fdaId") || "");

  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState("");

  const startFromLink = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    return query.get("start");
  }, []);

  useEffect(() => {
    if (!meetingId) return;
    let cancelled = false;
    const fetchInfo = async () => {
      setInfoLoading(true);
      setInfoError("");
      try {
        const res = await fetch(`${API_URL}/v2/meeting-info/${meetingId}`);
        if (cancelled) return;
        if (res.status === 404) {
          setInfoError("Meeting not found — the link may be expired or incorrect.");
          return;
        }
        if (res.status === 403) {
          setInfoError("This meeting has already ended.");
          return;
        }
        if (!res.ok) {
          setInfoError("Could not load meeting details. Please try again.");
          return;
        }
        const data = await res.json();
        setMeetingInfo(data);
      } catch {
        if (!cancelled) setInfoError("Network error — please check your connection.");
      } finally {
        if (!cancelled) setInfoLoading(false);
      }
    };
    fetchInfo();
    return () => { cancelled = true; };
  }, [meetingId, API_URL]);

  const isPrivate = meetingInfo?.meetingType === "private";
  const isNamePrefilled = !!localStorage.getItem("username") && displayName !== "";

  const handleJoin = async (e) => {
    e.preventDefault();
    setJoinError("");
    if (!displayName.trim()) return setJoinError("Please enter your display name.");
    if (isPrivate && !fdaId.trim()) return setJoinError("Please enter your User ID.");
    if (email.trim() && !isValidEmail(email))
      return setJoinError("Email looks invalid — leave it blank or enter a valid one.");

    const normalEmail = email.trim() ? email.trim().toLowerCase() : null;

    if (meetingInfo?.isScheduled && startFromLink) {
      const start = new Date(startFromLink.replace("T", " ") + ":00");
      const earlyJoinWindow = 10 * 60 * 1000;
      if (Date.now() < start.getTime() - earlyJoinWindow) {
        setJoinError("Meeting has not started yet — you can join up to 10 minutes early.");
        return;
      }
    }

    try {
      setIsJoining(true);
      const body = {
        username: displayName.trim(),
        room: meetingId,
        email: normalEmail,
        ...(isPrivate && { fdaId: fdaId.trim() }),
      };
      const res = await fetch(`${API_URL}/v2/join-room`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setJoinError(parseError(data)); return; }

      if (data.identity) {
        if (isValidEmail(data.identity)) {
          localStorage.setItem("userEmail", data.identity);
          localStorage.removeItem("guestIdentity");
        } else {
          localStorage.setItem("guestIdentity", data.identity);
          localStorage.removeItem("userEmail");
        }
      }
      if (normalEmail) localStorage.setItem("userEmail", normalEmail);
      localStorage.setItem("username", displayName.trim());

      navigate(`/v2/room/${meetingId}`, {
        state: {
          email: normalEmail,
          username: displayName.trim(),
          fdaId: isPrivate ? fdaId.trim() : null,
          prefetchedToken: data.token,
          prefetchedIdentity: data.identity,
          prefetchedRole: data.role,
          meetingAgenda: data.meetingAgenda,
        },
      });
    } catch {
      setJoinError("Network error. Please check your connection and try again.");
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="fm-page">
      <Ambient />
      <ThemeSlot />

      <div className="fm-page__inner" style={{ maxWidth: 460 }}>
        <Brand tagline="You've been invited to join a meeting." />

        <section className="fm-panel" style={{ width: "100%" }} aria-labelledby="jp-title">
          {infoLoading && (
            <div className="fm-blockstate">
              <Spinner size="lg" />
              <p>Loading meeting details…</p>
            </div>
          )}

          {!infoLoading && infoError && (
            <div className="fm-blockstate">
              <div className="fm-blockstate__icon" aria-hidden="true">⚠️</div>
              <h2 className="fm-panel__title" id="jp-title" style={{ fontSize: "var(--text-lg)" }}>
                Can't join this meeting
              </h2>
              <p style={{ maxWidth: "32ch" }}>{infoError}</p>
              <Button variant="secondary" onClick={() => navigate("/v2")}>← Back to home</Button>
            </div>
          )}

          {!infoLoading && !infoError && meetingInfo && (
            <>
              <div className="fm-panel__head">
                <span className="fm-panel__eyebrow">📹 Invitation</span>
                <h2 id="jp-title" className="fm-panel__title">Join the meeting</h2>
              </div>

              {/* Meeting info card */}
              <div className="fm-meeting-info">
                <div className="fm-meeting-info__left">
                  {meetingInfo.meetingAgenda ? (
                    <>
                      <span className="fm-meeting-info__label">Agenda</span>
                      <span className="fm-meeting-info__agenda">{meetingInfo.meetingAgenda}</span>
                    </>
                  ) : (
                    <span className="fm-meeting-info__agenda" style={{ color: "var(--text-muted)" }}>
                      No agenda set
                    </span>
                  )}
                  {meetingInfo.currentParticipants != null && (
                    <span className="fm-meeting-info__count">
                      👥 {meetingInfo.currentParticipants} in meeting
                    </span>
                  )}
                </div>
                <span className={`fm-badge fm-badge--${meetingInfo.meetingType}`}>
                  {meetingInfo.meetingType === "private" ? "🔒 Private" : "🌐 Public"}
                </span>
              </div>

              <form className="fm-form" onSubmit={handleJoin} noValidate>
                <Input
                  label="Display name"
                  required
                  placeholder="How should we call you?"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoComplete="name"
                  autoFocus={!isNamePrefilled}
                  hint={isNamePrefilled ? "✓ Saved from last session — edit if needed" : undefined}
                />

                <Input
                  label="Email"
                  type="email"
                  optional
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  hint="Needed to re-join if you get disconnected"
                />

                {isPrivate && (
                  <Input
                    label="User ID"
                    required
                    placeholder="Your authorised User ID"
                    value={fdaId}
                    onChange={(e) => setFdaId(e.target.value)}
                    autoComplete="off"
                    hint="Required for private access"
                  />
                )}

                {joinError && (
                  <div className="fm-inline-error" role="alert">
                    <span aria-hidden="true">⚠️</span>
                    <span>{joinError}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={isJoining}
                >
                  {isJoining ? (isPrivate ? "Verifying…" : "Joining…") : "Join meeting"}
                </Button>

                <Button type="button" variant="ghost" size="sm" fullWidth onClick={() => navigate("/v2")}>
                  ← Back to home
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   DEFAULT EXPORT — convenience for App.js
═══════════════════════════════════════════════════════════════════════ */
export default JoinPageV2;